import {api} from "@/shared/api";
import {FeatureModuleSelectionDialog} from "@/features/pricing/featureModuleSelectionDialog";
import * as Button from "@/shared/utils/button";
import {
	AvailableFeatureModulesResponse,
	FeatureModule,
	OnboardingDashboardAccountInfo,
	SaveTrialFeatureModulesResponse
} from "@/shared/timr";

export class FeatureModulesOnboardingWidget extends HTMLElement {

	private config: AvailableFeatureModulesResponse;
	private activeFeatureModules: FeatureModule[];

	public connectedCallback(): void {
		this.loadAccountInfo().done(info => {
			this.activeFeatureModules = info.activeFeatureModules;
		});
		this.loadFeatureModules().done(featureModules => {
			this.config = featureModules;
		});

		this.innerHTML = `
				<div class="panel panel-flat">
					<div class="panel-heading">
						<h4 class="panel-title">${messages["onboardingDashboard.account.featureModules.title"]}</h4>
					</div>
					<div class="panel-body">
						<div class="flex-row">
							<div class="flex-column-auto">
								<div class="form-horizontal">
									${messages["onboardingDashboard.account.mainFeatures"]}
									<div class="form-group mt-10">
										<div class="col-xs-12">
											<label class="checkbox-inline">${messages["admin.clientSettings.workingTimeEnabled"]}
												<input type="checkbox" data-working-time-checkbox="" disabled />
											</label>
										</div>
									</div>
									<div class="form-group">
										<div class="col-xs-12">
											<label class="checkbox-inline">${messages["admin.clientSettings.workingTimeRequestsEnabled"]}
												<input type="checkbox" data-working-time-requests-checkbox="" class="pt-0" disabled />
											</label>
										</div>
									</div>
									<div class="form-group">
										<div class="col-xs-12">
											<label class="checkbox-inline">${messages["admin.clientSettings.projectTimeEnabled"]}
												<input type="checkbox" data-project-time-checkbox="" disabled />
											</label>
										</div>
									</div>
									<div class="form-group">
										<div class="col-xs-12">
											<label class="checkbox-inline">${messages["admin.clientSettings.driveLogEnabled"]}
												<input type="checkbox" data-drive-log-checkbox="" disabled />
											</label>
										</div>
									</div>
									<a href="/timr/admin/settings.html" class="btn btn-primary mt-15" style="width: fit-content">
										${messages["pricing.customize.mainModules.edit"]}
									</a>
								</div>
							</div>
							<div class="flex-column-auto ml-40" style="justify-content: space-between;">
								<div class="flex-row">
									<div class="flex-column-auto">
										${messages["onboardingDashboard.account.featureModules.base"]}
										<ul class="feature-module-list mt-10" data-base-feature-module-list=""></ul>
									</div>
									<div class="flex-column-auto">
										${messages["onboardingDashboard.account.featureModules.addOn"]}
										<ul class="feature-module-list mt-10" data-add-on-feature-module-list=""></ul>
									</div>
								</div>
								<div>
									<div class="text-grey">
										<i class="fal fa-info-circle position-left"></i>${messages["onboardingDashboard.account.info"]}
									</div>
									<button id="selectFeatureModulesButton" class="btn btn-primary mt-15" style="width: fit-content">
										${messages["pricing.customize.changeModules"]}
									</button>
								</div>
							</div>
					</div></div>`;
		this.initEventListeners();
	}

	private initEventListeners(): void {
		this.querySelector("#selectFeatureModulesButton").addEventListener("click", (e) => {
			const btn = e.target as HTMLButtonElement;
			Button.setLoading(btn);
			this.openFeatureModuleSelectionDialog(btn);
		});
	}

	private loadFeatureModules(): JQuery.jqXHR<AvailableFeatureModulesResponse> {
		return api("pricing/getAvailableFeatureModules.json").get();
	}

	private loadAccountInfo(): JQuery.jqXHR<OnboardingDashboardAccountInfo> {
		return api("onboarding/accountInfo.json")
			.get()
			.done((data: OnboardingDashboardAccountInfo) => {
				const baseFeatureModuleList: HTMLUListElement = this.querySelector("[data-base-feature-module-list]");
				data.activeBaseFeatureModules.forEach((featureModule) => {
					const li: HTMLLIElement = document.createElement("li");
					li.textContent = featureModule.name;
					baseFeatureModuleList.appendChild(li);
				});

				const addOnFeatureModuleList: HTMLUListElement = this.querySelector("[data-add-on-feature-module-list]");
				if (data.activeAdditionalFeatureModules.length > 0) {
					data.activeAdditionalFeatureModules.forEach((featureModule) => {
						const li: HTMLLIElement = document.createElement("li");
						li.textContent = featureModule.name;
						addOnFeatureModuleList.appendChild(li);
					});
				} else {
					const span: HTMLSpanElement = document.createElement("span");
					span.classList.add("text-grey", "mt-10");
					span.innerHTML = `<i class="fal fa-folder-open position-left"></i>${messages["onboardingDashboard.account.featureModules.addOn.empty"]}`;
					addOnFeatureModuleList.parentNode.insertBefore(span, addOnFeatureModuleList);
				}
				this.toggleTimrFeatureCheckbox("working-time-checkbox", data.workingTimeEnabled);
				this.toggleTimrFeatureCheckbox("working-time-requests-checkbox", data.workingTimeRequestsEnabled);
				this.toggleTimrFeatureCheckbox("project-time-checkbox", data.projectTimeEnabled);
				this.toggleTimrFeatureCheckbox("drive-log-checkbox", data.driveLogEnabled);
			});
	}

	private onSaveTrialFeatureModules(selectedModules: FeatureModule[]): void {
		void api("pricing/saveTrialFeatureModules.json")
			.post({selectedModuleIds: selectedModules.map(fm => fm.id)})
			.done((response: SaveTrialFeatureModulesResponse) => {
				if (response.reLoginRequired) {
					window.location.href = "/timr/reLoginRequired.html";
				} else {
					window.location.reload();
				}
			});
	}

	private openFeatureModuleSelectionDialog(btn: HTMLButtonElement): void {
		FeatureModuleSelectionDialog.show(
			{
				baseFeatureModules: this.config.baseFeatureModules,
				extensionFeatureModules: this.config.extensionFeatureModules,
				activeFeatureModules: this.activeFeatureModules
			},
			selectedModules => this.onSaveTrialFeatureModules(selectedModules)
		);
		Button.resetLoading(btn);
	}

	private toggleTimrFeatureCheckbox(name: string, enabled?: boolean): void {
		const checkbox: HTMLInputElement = this.querySelector(`[data-${name}]`);
		if (enabled === null) {
			checkbox.closest(".form-group").remove();
		} else {
			checkbox.checked = Boolean(enabled);
		}
	}
}
